import type { Category, HeroSlide, Product } from "./storefront";

type ApiCategory = {
  id: string;
  name: string;
  slug: string;
  imageUrl: string | null;
};

type ApiBrand = {
  id: string;
  name: string;
  slug: string;
  imageUrl: string;
  sort: number;
};

type ApiProduct = {
  id: string;
  slug: string;
  name: string;
  brand: string;
  summary: string | null;
  unitPrice: number;
  listPrice: number | null;
  discount: number | null;
  cashDiscountAmount?: number;
  stockQty: number;
  isSoldOut: boolean;
  mainImageUrl: string | null;
  imageUrls: string[];
  descriptionHtml: string | null;
  options: Array<{ name: string; values: Array<{ label: string }> }>;
  reviewCount: number;
  inquiryCount: number;
  dealStartedAt?: string;
  dealExpiresAt?: string;
  shipping?: { name: string; mode: string; baseFee: number } | null;
  categories?: Array<{ id: string; name: string; slug: string }>;
};

type ApiHero = {
  id: string;
  title: string;
  imageUrl: string;
  linkUrl: string | null;
};

type ApiHomeSection = {
  id: string;
  key: string;
  type: string;
  title: string | null;
  subtitle: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  imageUrl: string | null;
  products: ApiProduct[];
};

export type HajinHomeSection = {
  id: string;
  key: string;
  type: string;
  title: string | null;
  subtitle: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  imageUrl: string | null;
  products: Product[];
};

export type HajinStorefrontData = {
  categories: Category[];
  brands: ApiBrand[];
  products: Product[];
  todayProducts: Array<Product & { dealStartedAt?: string; dealExpiresAt?: string }>;
  cashSpecialProducts: Product[];
  heroSlides: Array<HeroSlide & { imageUrl?: string; linkUrl?: string | null }>;
  sections: HajinHomeSection[];
};

function apiBase() {
  const configured = import.meta.env.VITE_HAJIN_STORE_API_BASE_URL?.trim();
  if (configured) return configured.replace(/\/$/, "");
  // Production store API is reverse-proxied on the public tenant host. Keeping
  // this same-origin avoids an admin-domain cookie/CORS split.
  if (typeof window !== "undefined" && window.location.hostname.endsWith("etlandonline.shop")) return window.location.origin;
  return null;
}

export function isHajinStoreApiEnabled() {
  return Boolean(apiBase());
}

function asProduct(product: ApiProduct): Product {
  return {
    id: product.id,
    slug: product.slug,
    brand: product.brand,
    name: product.name,
    price: product.unitPrice,
    salePrice: product.unitPrice,
    listPrice: product.listPrice ?? undefined,
    discount: product.discount ?? undefined,
    tone: "from-stone-100 to-stone-300",
    image: product.mainImageUrl ?? undefined,
    galleryImages: product.imageUrls,
    detailImages: [...(product.descriptionHtml?.matchAll(/<img\b[^>]*\bsrc=["']([^"']+)["']/gi) ?? [])].map((match) => match[1]),
    soldOut: product.isSoldOut,
    delivery: product.summary ?? undefined,
    shippingFee: Number(product.shipping?.baseFee || 0),
    optionName: product.options[0]?.name,
    options: product.options[0]?.values.map((value) => value.label),
    reviewCount: product.reviewCount,
    questionCount: product.inquiryCount,
    categorySlugs: product.categories?.map((category) => category.slug),
    categoryNames: product.categories?.map((category) => category.name),
  };
}

function asTodayProduct(product: ApiProduct) {
  return { ...asProduct(product), dealStartedAt: product.dealStartedAt, dealExpiresAt: product.dealExpiresAt };
}

function asCashSpecialProduct(product: ApiProduct): Product {
  const basePrice = Math.max(0, Number(product.unitPrice || 0));
  const discountAmount = Math.min(Math.max(0, Math.floor(Number(product.cashDiscountAmount || 0))), Math.max(0, basePrice - 1));
  if (!discountAmount || !basePrice) return asProduct(product);
  const cashPrice = basePrice - discountAmount;
  return {
    ...asProduct(product),
    price: cashPrice,
    salePrice: cashPrice,
    listPrice: basePrice,
    discount: Math.max(1, Math.round((discountAmount / basePrice) * 100)),
  };
}

function asHero(hero: ApiHero, index: number): HeroSlide & { imageUrl?: string; linkUrl?: string | null } {
  const arts: HeroSlide["art"][] = ["city", "laundry", "kitchen", "event", "living", "digital"];
  return {
    eyebrow: "HAJIN STORE",
    title: hero.title,
    detail: "하진이 제안하는 오늘의 혜택",
    tone: "from-stone-100 via-slate-200 to-stone-300",
    art: arts[index % arts.length],
    imageUrl: hero.imageUrl,
    linkUrl: hero.linkUrl,
  };
}

/**
 * When no API base is configured, the captured storefront snapshot remains
 * untouched. This keeps visual-reference work deterministic in local mode.
 */
export async function loadHajinStorefront(): Promise<HajinStorefrontData | null> {
  const base = apiBase();
  if (!base) return null;
  const response = await fetch(`${base}/api/store/hajin/bootstrap`, { credentials: "include", cache: "no-store" });
  if (!response.ok) throw new Error(`HAJIN_STORE_BOOTSTRAP_${response.status}`);
  const payload = await response.json() as { categories?: ApiCategory[]; brands?: ApiBrand[]; products?: ApiProduct[]; todayProducts?: ApiProduct[]; cashSpecial?: { products?: ApiProduct[] }; home?: { hero?: ApiHero[]; sections?: ApiHomeSection[] } };
  const categories = (payload.categories ?? []).map((category, index) => ({
    slug: category.slug,
    name: category.name,
    icon: category.imageUrl ? "●" : ["▣", "◫", "◌", "☼"][index % 4],
  }));
  const products = (payload.products ?? []).map(asProduct);
  const heroSlides = (payload.home?.hero ?? []).map(asHero);
  const sections = (payload.home?.sections ?? []).map((section) => ({
    ...section,
    products: (section.products ?? []).map(asProduct),
  }));
  if (!categories.length && !products.length && !heroSlides.length && !sections.length) return null;
  return {
    categories,
    brands: payload.brands ?? [],
    products,
    todayProducts: (payload.todayProducts ?? []).map(asTodayProduct),
    cashSpecialProducts: (payload.cashSpecial?.products ?? []).map(asCashSpecialProduct),
    heroSlides,
    sections,
  };
}

export async function loadHajinProducts(params: { categorySlug?: string; q?: string; sort?: "priceAsc" | "priceDesc" } = {}): Promise<Product[] | null> {
  const base = apiBase();
  if (!base) return null;
  const search = new URLSearchParams();
  if (params.categorySlug) search.set("categorySlug", params.categorySlug);
  if (params.q) search.set("q", params.q);
  if (params.sort) search.set("sort", params.sort);
  // Public category/search pages render their own local filters and paging.
  // 60 rows made facet counts look inconsistent (e.g. a category total could
  // be larger than the visible list), so keep the fetched result window wide
  // enough for the imported Etland category/search shelves while still bounded.
  search.set("pageSize", "240");
  const response = await fetch(`${base}/api/store/hajin/products?${search.toString()}`, { credentials: "include" });
  if (!response.ok) throw new Error(`HAJIN_PRODUCTS_${response.status}`);
  const payload = await response.json() as { rows?: ApiProduct[]; products?: ApiProduct[] };
  return (payload.rows ?? payload.products ?? []).map(asProduct);
}

export async function loadHajinCategoryProducts(categorySlug: string, q?: string) {
  return loadHajinProducts({ categorySlug, q });
}

export async function loadHajinProduct(slug: string): Promise<Product | null> {
  const base = apiBase();
  if (!base) return null;
  const response = await fetch(`${base}/api/store/hajin/products/${encodeURIComponent(slug)}`, { credentials: "include" });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`HAJIN_PRODUCT_${response.status}`);
  const payload = await response.json() as { product?: ApiProduct };
  return payload.product ? asProduct(payload.product) : null;
}

async function postHajinProductFeedback(path: string, payload: Record<string, unknown>, method = "POST") {
  const base = apiBase();
  if (!base) throw new Error("HAJIN_STORE_API_DISABLED");
  const response = await fetch(`${base}${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({})) as { message?: string; error?: string };
  if (!response.ok) throw new Error(body.message || body.error || `HAJIN_FEEDBACK_${response.status}`);
  return body;
}

export type HajinProductReview = {
  id: string;
  authorName: string;
  title: string | null;
  content: string;
  rating: number;
  imageUrls: string[];
  createdAt: string;
  updatedAt?: string;
  canManage?: boolean;
};

export async function loadHajinProductReviews(slug: string): Promise<HajinProductReview[]> {
  const base = apiBase();
  if (!base) return [];
  const response = await fetch(`${base}/api/store/hajin/products/${encodeURIComponent(slug)}/reviews`, { credentials: "include" });
  if (!response.ok) return [];
  const payload = await response.json().catch(() => ({})) as { rows?: HajinProductReview[] };
  return Array.isArray(payload.rows) ? payload.rows : [];
}

export async function uploadHajinProductReviewMedia(file: File): Promise<string> {
  const base = apiBase();
  if (!base) throw new Error("HAJIN_STORE_API_DISABLED");
  const form = new FormData();
  form.set("file", file);
  const response = await fetch(`${base}/api/store/hajin/review-upload`, { method: "POST", credentials: "include", body: form });
  const body = await response.json().catch(() => ({})) as { url?: string; message?: string; error?: string };
  if (!response.ok || !body.url) throw new Error(body.message || body.error || `HAJIN_REVIEW_UPLOAD_${response.status}`);
  return body.url;
}

export async function submitHajinProductReview(slug: string, payload: { rating: number; title?: string; content: string; imageUrls?: string[] }) {
  return postHajinProductFeedback(`/api/store/hajin/products/${encodeURIComponent(slug)}/reviews`, payload);
}

export async function updateHajinProductReview(slug: string, reviewId: string, payload: { rating: number; title?: string; content: string; imageUrls?: string[] }) {
  return postHajinProductFeedback(`/api/store/hajin/products/${encodeURIComponent(slug)}/reviews`, { reviewId, ...payload }, "PATCH");
}

export async function deleteHajinProductReview(slug: string, reviewId: string) {
  return postHajinProductFeedback(`/api/store/hajin/products/${encodeURIComponent(slug)}/reviews`, { reviewId }, "DELETE");
}

export type HajinProductInquiry = {
  id: string;
  subject: string;
  content: string | null;
  isSecret: boolean;
  isLocked: boolean;
  status: string;
  hasAnswer: boolean;
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
  authorName: string;
  canManage?: boolean;
  secretNotice?: string | null;
};

export async function loadHajinProductInquiries(slug: string): Promise<HajinProductInquiry[]> {
  const base = apiBase();
  if (!base) return [];
  const response = await fetch(`${base}/api/store/hajin/products/${encodeURIComponent(slug)}/inquiries`, { credentials: "include" });
  if (!response.ok) return [];
  const payload = await response.json().catch(() => ({})) as { rows?: HajinProductInquiry[] };
  return Array.isArray(payload.rows) ? payload.rows : [];
}

export async function unlockHajinProductInquiry(slug: string, inquiryId: string, password: string): Promise<HajinProductInquiry> {
  const base = apiBase();
  if (!base) throw new Error("HAJIN_STORE_API_DISABLED");
  const response = await fetch(`${base}/api/store/hajin/products/${encodeURIComponent(slug)}/inquiries`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ inquiryId, password }),
  });
  const body = await response.json().catch(() => ({})) as { inquiry?: HajinProductInquiry; message?: string; error?: string };
  if (!response.ok || !body.inquiry) throw new Error(body.message || body.error || `HAJIN_INQUIRY_UNLOCK_${response.status}`);
  return body.inquiry;
}

export async function submitHajinProductInquiry(slug: string, payload: { subject: string; content: string; isSecret?: boolean; password?: string }) {
  return postHajinProductFeedback(`/api/store/hajin/products/${encodeURIComponent(slug)}/inquiries`, payload);
}

export async function updateHajinProductInquiry(slug: string, inquiryId: string, payload: { subject: string; content: string; isSecret?: boolean; password?: string }) {
  return postHajinProductFeedback(`/api/store/hajin/products/${encodeURIComponent(slug)}/inquiries`, { mode: "update", inquiryId, ...payload }, "PATCH");
}

export async function deleteHajinProductInquiry(slug: string, inquiryId: string) {
  return postHajinProductFeedback(`/api/store/hajin/products/${encodeURIComponent(slug)}/inquiries`, { inquiryId }, "DELETE");
}
